import React, { useEffect, useRef, useState } from 'react';
import posed, { PoseGroup } from 'react-pose';
import { Map } from './components/map';
import { SidePanel, SidePanelContainer } from './components/side-panel';
import { Location, Router, navigate, WindowLocation } from '@reach/router';
import { useStateValue } from './components/state';
import { NavBar, NavBarItem } from './components/nav-bar';
import { Icon } from './interfaces';
import { Where } from './views/where';
import { How } from './views/how';
import { Why } from './views/why';
import { Reuse } from './views/reuse';
import { Desalination } from './views/where/desalination';
import { WhereHome } from './views/where/home';
import { markers, afterMarkers } from './data/markers';
import { GroundWater } from './views/where/groundwater';
import { Dams } from './views/where/dams';
import { GroundWaterReplenishment } from './views/where/groundwater-replenishment';
import { ReuseHome } from './views/reuse/home';
import { RecycledIrrigation } from './views/reuse/recycled-irrigation';
import { useMatchMedia } from './hooks/use-match-media';
import breakpoints from './sass-utils/breakpoints.module.scss';
import { RecycledIndustry } from './views/reuse/recycled-industry';
import { GroundwaterReplenishment } from './views/reuse/groundwater-replenishment';
import { OceanOutfall } from './views/reuse/ocean-outfall';
import styles from './app.module.scss';
import { TagManager } from './components/tag-manager';

const navItems: Array<{ title: string; icon: Icon; link: string }> = [
  { title: 'Where', icon: 'drops', link: '/where' },
  { title: 'Why', icon: 'drop', link: '/why' },
  { title: 'How', icon: 'tap', link: '/how' },
  { title: 'Reuse', icon: 'recycle', link: '/reuse' },
];

const RoutesContainer = posed.div({
  enter: {
    opacity: 1,
    delay: 100,
    beforeChildren: true,
    transition: {
      duration: 10,
    },
  },
  exit: {
    opacity: 0,
  },
});

const PosedRouter: React.FC<{ location: WindowLocation }> = ({ children, location }) => (
  <Location>
    {({ location }) => (
      <PoseGroup>
        <RoutesContainer key={location.key}>
          <Router location={location} primary={false}>
            {children}
          </Router>
        </RoutesContainer>
      </PoseGroup>
    )}
  </Location>
);

const App: React.FC = () => {
  const [state, dispatch] = useStateValue();
  const isDesktop = useMatchMedia(`(min-width: ${breakpoints.medium})`);
  const mapHeightMonitorEl = useRef<HTMLDivElement>(null);
  const [browserNavBarHeight, setBrowserNavBarHeight] = useState(0);

  // Navigate to home if the user hasn't entered an address yet
  useEffect(() => {
    if (state.map.geolocationIntro) {
      navigate('/');
      dispatch({ type: 'resetState' });
    }
  }, [dispatch, state.map.geolocationIntro]);

  // Get the height of the browser nav bar
  useEffect(() => {
    if (mapHeightMonitorEl.current) {
      const mapHolderHeight = getComputedStyle(mapHeightMonitorEl.current).height;
      const browserNavBarHeight = mapHolderHeight ? parseFloat(mapHolderHeight) - window.innerHeight : 0;
      document.body.style.setProperty('--browserNavBarHeight', `${browserNavBarHeight}px`);
      setBrowserNavBarHeight(browserNavBarHeight);
    }
  }, []);

  // Set the ready state of the map once the user has entered an address
  const onAddressChange = (address: any) => {
    if (address) {
      // Navigate to where after first search
      if (state.map.geolocationIntro) {
        navigate('/where');
      }

      dispatch({ type: 'setMapReady' });
    }
  };

  // When skipping the search
  const onSkipSearch = () => {
    navigate('/where');
    dispatch({ type: 'setMapReady' });
  };

  // Set the active set of markers on the map
  const activeMarkers = state.map.activeMarkerSet === 'default' ? markers : afterMarkers;

  return (
    <Location>
      {({ location }) => (
        <TagManager location={location}>
          <SidePanelContainer>
            <Map
              activeMarkerType={state.map.activeMarkerType}
              activeYear={state.map.activeYear}
              geolocationIntro={state.map.geolocationIntro}
              geolocation={state.map.geolocation}
              onGeolocation={onAddressChange}
              markers={activeMarkers}
              activeMarkerSet={state.map.activeMarkerSet}
              isDesktop={isDesktop}
              browserNavBarHeight={browserNavBarHeight}
              onSkipSearch={onSkipSearch}
            />
            <SidePanel open={state.sidePanelOpen} full={state.sidePanelFull} location={location}>
              {previousLocation => (
                <PosedRouter location={location}>
                  <Where path="where" previousLocation={previousLocation}>
                    <WhereHome path="/" />
                    <Desalination path="desalination" />
                    <GroundWater path="groundwater" />
                    <Dams path="dams" />
                    <GroundWaterReplenishment path="groundwater-replenishment" />
                  </Where>
                  <Why path="why" />
                  <How path="how" />
                  <Reuse path="reuse">
                    <ReuseHome path="/" />
                    <RecycledIrrigation path="recycled-irrigation" />
                    <RecycledIndustry path="recycled-industry" />
                    <GroundwaterReplenishment path="groundwater-replenishment" />
                    <OceanOutfall path="ocean-outfall" />
                  </Reuse>
                </PosedRouter>
              )}
            </SidePanel>
            {state.navBarVisible && (
              <NavBar>
                {navItems.map(({ title, icon, link }) => (
                  <NavBarItem key={title} title={title} icon={icon} link={link} />
                ))}
              </NavBar>
            )}
          </SidePanelContainer>
          <div className={styles.mapHeightMonitor} ref={mapHeightMonitorEl} />
        </TagManager>
      )}
    </Location>
  );
};

export default App;
